import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export const InquiryWidget = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.email.trim()) err.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) err.email = "Enter a valid email";
    if (!form.phone.trim()) err.phone = "Phone is required";
    if (!form.subject.trim()) err.subject = "Subject is required";
    if (!form.message.trim()) err.message = "Message is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      await res.json();
      toast.success("Thanks! We got your inquiry — we'll be in touch soon. 🍦");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border px-4 py-2.5 text-sm text-[#2A1620] bg-white placeholder:text-[#B79AA6] focus:outline-none focus:ring-2 focus:ring-[#F63C8B]/40 transition";
  const errCls = "border-[#F63C8B]";
  const okCls = "border-[#F1D7E2]";

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="inquiry-fab"
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0.6, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        aria-label="Open inquiry form"
        className="fixed bottom-5 right-5 z-[70] inline-flex items-center gap-2 btn-primary px-5 py-3.5 text-sm font-bold uppercase tracking-[0.1em]">
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        <span className="hidden sm:inline">{open ? "Close" : "Inquiry"}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[68] bg-black/30 backdrop-blur-sm sm:hidden" aria-hidden="true" />
            <motion.div
              data-testid="inquiry-panel"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="fixed z-[69] bottom-24 right-4 left-4 sm:left-auto sm:right-5 sm:w-[400px] card overflow-hidden">
            <div className="bg-[#F63C8B] text-white px-6 py-5">
              <h3 className="font-display text-2xl leading-none">Send an Inquiry</h3>
              <p className="text-white/85 text-sm mt-1">Questions, catering or celebrations — we'd love to help!</p>
            </div>
            <form onSubmit={submit} noValidate className="p-5 sm:p-6 space-y-3 max-h-[62vh] overflow-y-auto">
              <div>
                <input data-testid="inquiry-name" value={form.name} onChange={set("name")} placeholder="Name"
                  className={`${field} ${errors.name ? errCls : okCls}`} />
                {errors.name && <p className="mt-1 text-xs text-[#F63C8B]">{errors.name}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <input data-testid="inquiry-email" value={form.email} onChange={set("email")} placeholder="Email" type="email"
                    className={`${field} ${errors.email ? errCls : okCls}`} />
                  {errors.email && <p className="mt-1 text-xs text-[#F63C8B]">{errors.email}</p>}
                </div>
                <div>
                  <input data-testid="inquiry-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" type="tel"
                    className={`${field} ${errors.phone ? errCls : okCls}`} />
                  {errors.phone && <p className="mt-1 text-xs text-[#F63C8B]">{errors.phone}</p>}
                </div>
              </div>
              <div>
                <input data-testid="inquiry-subject" value={form.subject} onChange={set("subject")} placeholder="Subject"
                  className={`${field} ${errors.subject ? errCls : okCls}`} />
                {errors.subject && <p className="mt-1 text-xs text-[#F63C8B]">{errors.subject}</p>}
              </div>
              <div>
                <textarea data-testid="inquiry-message" value={form.message} onChange={set("message")} placeholder="Your message" rows={4}
                  className={`${field} resize-none ${errors.message ? errCls : okCls}`} />
                {errors.message && <p className="mt-1 text-xs text-[#F63C8B]">{errors.message}</p>}
              </div>
              <button data-testid="inquiry-submit" type="submit" disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.12em] disabled:opacity-70">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Submit Inquiry</>}
              </button>
            </form>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
